<template>
  <div
    id="editor"
    class="editor"
    :class="{ edit: isEdit }"
    :style="{
      width: (isChildPageCanvas ? childPageStyle.pageWidth : changeStyleWithScale(projectStyleData.width)) + 'px',
      height: (isChildPageCanvas ? childPageStyle.pageHeight : changeStyleWithScale(projectStyleData.height)) + 'px'
    }"
    @contextmenu.capture.prevent="handleContextMenu"
    @mousedown="handleMouseDown"
  >
    <div class="canvas-size-marks" :style="canvasSizeMarksStyle">
      <div class="canvas-size-w">
        <span>{{ canvasSizeLabelW }}</span>
        <i class="canvas-size-tick canvas-size-tick-h" />
      </div>
      <div class="canvas-size-h">
        <span>{{ canvasSizeLabelH }}</span>
        <i class="canvas-size-tick canvas-size-tick-v" />
      </div>
    </div>
    <!-- 网格线 -->
    <Grid />

    <!--页面组件列表展示（视口外不挂载 DOM，减轻大量组件时的卡顿；数据结构不变）-->
    <Shape
      v-for="item in visibleComponentsForRender"
      :currentScale="currentScale"
      :key="item.id"
      :default-style="item.style"
      :style="getShapeStyle(item.style)"
      :active="item.id === (curComponent || {}).id"
      :element="item"
      :isEcological="item.compType === ECOLOGICAL_SIGN"
      :index="usableComponentIndexById[item.id] != null ? usableComponentIndexById[item.id] : 0"
      :class="{
        lock: item.isLock,
        commonZ: true,
        'shape-multi-selected': isComponentMultiSelected(item.id)
      }"
    >
      <component
        :is="item.component"
        v-if="item.component != 'v-text'"
        :key="carouselEditorRenderKey(item)"
        :id="'component' + item.id"
        class="component"
        :style="getComponentStyle(item.style, item)"
        :prop-value="item.propValue"
        :styleDatas="item.style"
        :componentIndex="usableComponentIndexById[item.id] != null ? usableComponentIndexById[item.id] : 0"
        :element="item"
      />

      <component
        :is="item.component"
        v-else
        :id="'component' + item.id"
        class="component"
        :style="getComponentStyle(item.style)"
        :prop-value="item.propValue"
        :element="item"
        @input="handleInput"
      />
    </Shape>
    <!-- 右击菜单 -->
    <!-- <ContextMenu /> -->
    <!-- 标线 -->
    <MarkLine />
    <!-- 选中区域 -->
    <Area v-show="isShowArea" :currentScale="currentScale" :start="start" :width="width" :height="height" @areaMove="handleAreaMove" />
    <div
      v-if="selectTypeVisible"
      class="select-type-panel"
      :style="selectTypePanelStyle"
      @mousedown.stop
    >
      <div class="select-type-title">{{ $tt("选择要保留的类型") }}</div>
      <button
        class="select-type-btn all"
        type="button"
        @click="confirmSelectType(null)"
      >
        {{ $tt("全部") }}（{{ pendingSelectComponents.length }}）
      </button>
      <button
        v-for="opt in selectTypeOptions"
        :key="opt.key"
        class="select-type-btn"
        type="button"
        @click="confirmSelectType(opt.key)"
      >
        {{ $tt(opt.label) }}（{{ opt.count }}）
      </button>
      <button
        class="select-type-btn cancel"
        type="button"
        @click="cancelSelectType"
      >
        {{ $tt("取消框选") }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import Shape from "./Shape";
import { getStyle, getComponentRotatedStyle, withTransparentButtonColor } from "@/utils/style";
import { isEmpty, clientPointToCanvas } from "@/utils/utils";
import ContextMenu from "./ContextMenu";
import MarkLine from "./MarkLine";
import Area from "./Area";
import eventBus from "@/utils/eventBus";
import Grid from "./Grid";
import { changeStyleWithScale } from "@/utils/translate";
import { isTypingTarget, isCanvasPanHotkey, isTextInputActive } from "@/utils/shortcutKey";
import { ECOLOGICAL_SIGN } from '@/constants/editor';
import { carouselEditorRenderKey as buildCarouselEditorRenderKey } from "@/utils/carouselExt";
import { Loading } from "element-ui";

export default {
  components: { Shape, ContextMenu, MarkLine, Area, Grid },
  props: {
    isEdit: {
      type: Boolean,
      default: true,
    },
    isInitLoaded: {
      type: Boolean
    },
    /** 项目已加载后切换页面会 remount Editor，不再套一层全屏 loading */
    skipInitialLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentScale: 1,
      boxdeltaY: null,
      isSpaceNoShowArea: false, // 这个用来判断是否显示Area组件
      editorX: 0,
      editorY: 0,
      start: {
        // 选中区域的起点
        x: 0,
        y: 0,
      },
      width: 0,
      height: 0,
      isShowArea: false,
      selectTypeVisible: false,
      selectTypeOptions: [],
      pendingSelectComponents: [],
      ECOLOGICAL_SIGN,
      loadingInstance: null,
      loadingTexts: ['正在初始化画布...', '请稍等...', '数据加载中...', '启动画布渲染器...', '即将完成画布渲染...'],
      currentTextIndex: 0,
      loadingSafetyTimer: null,
      /** 与滚轮缩放闭包共用，便于切换页面时恢复缩放 */
      _canvasScaleRef: null,
      /** 随平移/缩放递增，驱动视口裁剪列表刷新（left/top 不在响应式 data 中） */
      canvasViewRevision: 0,
      _bumpViewRaf: null,
    };
  },
  computed: {
    ...mapState([
      "componentData", 
      "curComponent", 
      "canvasStyleData", 
      "projectStyleData", 
      "editor", 
      "areaData",
      "selectedCurrentPage",
      "pageOptions",
    ]),
    /** 以当前画布 Back 为准，避免切换页时 store.isChildPage 先于 componentData 更新导致主/子画布尺寸与组件数据不一致 */
    isChildPageCanvas() {
      const back = this.componentData.find((a) => a.id === "Back");
      return !!(back && back.isChildPage);
    },
    usableComponentsData() {
      const ret = this.componentData.filter((c) => c.id != "Back");
      return ret;
    },
    /** 裁剪列表渲染时 :index / componentIndex 仍需与全量列表一致（含 Back） */
    usableComponentIndexById() {
      const list = this.componentData || [];
      const map = Object.create(null);
      for (let i = 0; i < list.length; i++) {
        if (list[i] && list[i].id != null) {
          map[list[i].id] = i;
        }
      }
      return map;
    },
    childPageStyle() {
      var pageStyle = this.componentData.find((a) => a.id === 'Back');
      return pageStyle && pageStyle.style;
    },
    /** 画布逻辑尺寸（与模板中 editor 宽高一致） */
    editorCanvasWidthPx() {
      if (this.isChildPageCanvas && this.childPageStyle) {
        return this.childPageStyle.pageWidth || 1200;
      }
      return this.changeStyleWithScale(this.projectStyleData.width);
    },
    editorCanvasHeightPx() {
      if (this.isChildPageCanvas && this.childPageStyle) {
        return this.childPageStyle.pageHeight || 740;
      }
      return this.changeStyleWithScale(this.projectStyleData.height);
    },
    canvasSizeLabelW() {
      if (this.isChildPageCanvas && this.childPageStyle) {
        return Number(this.childPageStyle.pageWidth) || 0;
      }
      return Number(this.projectStyleData && this.projectStyleData.width) || 0;
    },
    canvasSizeLabelH() {
      if (this.isChildPageCanvas && this.childPageStyle) {
        return Number(this.childPageStyle.pageHeight) || 0;
      }
      return Number(this.projectStyleData && this.projectStyleData.height) || 0;
    },
    canvasSizeMarksStyle() {
      const s = Number(this.currentScale) || 1;
      const inv = s > 0 ? 1 / s : 1;
      return {
        transform: `scale(${inv})`,
        transformOrigin: "0 0",
      };
    },
    /**
     * 仅渲染视口附近组件，显著减少 Shape/自定义组件实例数量。
     * 框选拖拽、多选区域操作时不裁剪，避免选区与命中异常。
     */
    visibleComponentsForRender() {
      void this.canvasViewRevision;
      const list = this.usableComponentsData;
      const n = list.length;
      if (n < 48 || this.isShowArea) {
        return list;
      }
      const bounds = this.getViewportCullBounds();
      if (!bounds) {
        return list;
      }
      const curId = this.curComponent && this.curComponent.id;
      const out = [];
      for (let i = 0; i < n; i++) {
        const item = list[i];
        if (this.shouldRenderComponentInViewport(item, bounds, curId)) {
          out.push(item);
        }
      }
      return out.length ? out : list;
    },
    selectTypePanelStyle() {
      return {
        left: `${this.start.x + this.width / 2}px`,
        top: `${this.start.y + this.height / 2}px`,
        transform: "translate(-50%, -50%)",
      };
    },
  },
  watch: {
    isInitLoaded: {
      handler: function(val) {
        if (val) {
          this.hideLoading();
        }
      }
    }
  },
  created() {
    if (!this.skipInitialLoading) {
      this.showLoading();
    }
    const _this = this;
    this._docKeydown = function(e) {
      _this.keydownHandle(e);
    };
    document.onkeydown = this._docKeydown;
  },
  mounted() {
    // 获取编辑器元素
    // this.setEditorReSize();
    this.$store.commit("getEditor");
    this.bindCanvasInteraction();
    this._hideAreaHandler = () => {
      this.hideArea();
    };
    eventBus.$on("hideArea", this._hideAreaHandler);
    this._canvasAiTypingHandler = () => {
      if (this._canvasInteractHandles && this._canvasInteractHandles.resetSpacePan) {
        this._canvasInteractHandles.resetSpacePan();
        return;
      }
      this.isSpaceNoShowArea = false;
      const el = this.$el;
      if (el) {
        el.removeAttribute("data-space-pan");
        el.style.cursor = "default";
      }
    };
    eventBus.$on("canvasAiTyping", this._canvasAiTypingHandler);
    this._toggleShiftSelectHandler = (payload) => {
      this.toggleShiftSelect(payload);
    };
    eventBus.$on("toggleShiftSelect", this._toggleShiftSelectHandler);
    this.ensureDefaultViewportOnMount();
    setTimeout(() => {
      if (this.loadingInstance) {
        this.loadingInstance.setText(this.loadingTexts[4]);
      }
      this.isInitLoaded && this.hideLoading();
    }, 1000);
  },
  beforeDestroy() {
    this.hideLoading();
    if (document.onkeydown === this._docKeydown) {
      document.onkeydown = null;
    }
    this._docKeydown = null;
    if (this._bumpViewRaf != null) {
      cancelAnimationFrame(this._bumpViewRaf);
      this._bumpViewRaf = null;
    }
    this.unbindCanvasInteraction();
    if (this._hideAreaHandler) {
      eventBus.$off("hideArea", this._hideAreaHandler);
    }
    if (this._canvasAiTypingHandler) {
      eventBus.$off("canvasAiTyping", this._canvasAiTypingHandler);
    }
    if (this._toggleShiftSelectHandler) {
      eventBus.$off("toggleShiftSelect", this._toggleShiftSelectHandler);
    }
  },
  methods: {
    changeStyleWithScale,
    carouselEditorRenderKey(item) {
      return buildCarouselEditorRenderKey(item);
    },
    /**
     * 编辑器首次挂载时落到默认视口。切页不再重挂 Editor，缩放/平移由 VisualDrag 按页恢复。
     */
    ensureDefaultViewportOnMount() {
      const tryApply = (n) => {
        const el = this.$el;
        if (!el) {
          if (n < 120) {
            requestAnimationFrame(() => tryApply(n + 1));
          }
          return;
        }
        const host =
          typeof document !== "undefined"
            ? document.querySelector(".home main .center")
            : null;
        const c =
          typeof document !== "undefined"
            ? document.getElementById("content")
            : null;
        const hostRect = host ? host.getBoundingClientRect() : null;
        const ready =
          (hostRect && hostRect.width >= 2 && hostRect.height >= 2) ||
          (c && c.clientWidth >= 2 && c.clientHeight >= 2);
        if (!ready && n < 120) {
          requestAnimationFrame(() => tryApply(n + 1));
          return;
        }
        this.applyViewportState(this.getDefaultCenteredViewportState(1));
      };
      requestAnimationFrame(() => tryApply(0));
    },
    scheduleCanvasViewBump() {
      if (this._bumpViewRaf != null) {
        return;
      }
      this._bumpViewRaf = requestAnimationFrame(() => {
        this._bumpViewRaf = null;
        this.canvasViewRevision++;
      });
    },
    getViewportCullBounds() {
      const ed = this.$el;
      const parent = ed && ed.offsetParent;
      if (!ed || !parent) {
        return null;
      }
      const L = parseFloat(ed.style.left) || 0;
      const T = parseFloat(ed.style.top) || 0;
      const S = this.currentScale || 1;
      const W = parent.clientWidth;
      const H = parent.clientHeight;
      const marginScreen = 160;
      const m = marginScreen / S;
      const cw = this.editorCanvasWidthPx;
      const ch = this.editorCanvasHeightPx;
      let x0 = (-L) / S - m;
      let y0 = (-T) / S - m;
      let x1 = (W - L) / S + m;
      let y1 = (H - T) / S + m;
      x0 = Math.max(0, Math.min(cw, x0));
      y0 = Math.max(0, Math.min(ch, y0));
      x1 = Math.max(0, Math.min(cw, x1));
      y1 = Math.max(0, Math.min(ch, y1));
      if (x1 < x0) {
        x1 = cw;
      }
      if (y1 < y0) {
        y1 = ch;
      }
      return { x0, y0, x1, y1 };
    },
    boxIntersectsCullView(view, styleBox) {
      return !(
        styleBox.right < view.x0 ||
        styleBox.left > view.x1 ||
        styleBox.bottom < view.y0 ||
        styleBox.top > view.y1
      );
    },
    ecologicalContainsSelectedId(item, curId) {
      if (!curId || !item || item.compType !== ECOLOGICAL_SIGN) {
        return false;
      }
      const pv = item.propValue;
      if (!pv || !Array.isArray(pv)) {
        return false;
      }
      for (let i = 0; i < pv.length; i++) {
        const c = pv[i];
        if (c && c.id === curId) {
          return true;
        }
      }
      return false;
    },
    shouldRenderComponentInViewport(item, bounds, curId) {
      if (!item) {
        return true;
      }
      if (curId && (item.id === curId || this.ecologicalContainsSelectedId(item, curId))) {
        return true;
      }
      try {
        const style = getComponentRotatedStyle({ ...item.style });
        return this.boxIntersectsCullView(bounds, style);
      } catch (e) {
        return true;
      }
    },
    resetEditor() {
      this.currentScale = 1;
      if (this._canvasScaleRef) {
        this._canvasScaleRef.value = 1;
      }
      this.isShowArea = false;
      this.isSpaceNoShowArea = false;
      if (this.$el) {
        this.$el.style.transform = "scale(1)";
        this.$el.setAttribute("data-scale", "1");
      }
    },
    getViewportState() {
      const el = this.$el;
      const scale = this._canvasScaleRef
        ? this._canvasScaleRef.value
        : this.currentScale || 1;
      return {
        scale: Number(scale),
        left: el && el.style.left ? el.style.left : "0px",
        top: el && el.style.top ? el.style.top : "0px",
      };
    },
    applyViewportState(state) {
      if (!this.$el || !state) {
        return;
      }
      let s = Number(state.scale);
      if (!isFinite(s) || s <= 0) {
        s = 1;
      }
      if (this._canvasScaleRef) {
        this._canvasScaleRef.value = s;
      }
      this.currentScale = s;
      this.$el.style.left =
        state.left != null && state.left !== "" ? state.left : "0px";
      this.$el.style.top =
        state.top != null && state.top !== "" ? state.top : "0px";
      this.$el.style.transformOrigin = "0 0";
      this.$el.style.transform = `scale(${s})`;
      this.$el.setAttribute("data-scale", String(s));
      this.scheduleCanvasViewBump();
    },
    getVisibleWorkspaceBox() {
      const contentEl =
        (typeof document !== "undefined" && document.getElementById("content")) ||
        (this.$el && this.$el.parentElement);
      const home =
        typeof document !== "undefined"
          ? document.querySelector(".home")
          : null;
      const centerHost =
        typeof document !== "undefined"
          ? document.querySelector(".home main .center")
          : null;
      const host = contentEl || centerHost;
      if (!host) {
        return null;
      }
      const hostRect = host.getBoundingClientRect();
      let x0 = hostRect.left;
      let x1 = hostRect.right;
      let y0 = hostRect.top;
      let y1 = hostRect.bottom;
      const leftCollapsed = !!(home && home.classList.contains("left-collapsed"));
      const rightCollapsed = !!(home && home.classList.contains("right-collapsed"));
      const leftEl =
        typeof document !== "undefined"
          ? document.querySelector(".home main .left")
          : null;
      const rightEl =
        typeof document !== "undefined"
          ? document.querySelector(".home main .right")
          : null;
      if (!leftCollapsed && leftEl && leftEl.offsetWidth > 8) {
        x0 = Math.max(x0, leftEl.getBoundingClientRect().right);
      }
      if (!rightCollapsed && rightEl && rightEl.offsetWidth > 8) {
        x1 = Math.min(x1, rightEl.getBoundingClientRect().left);
      }
      const pw = x1 - x0;
      const ph = y1 - y0;
      if (pw < 2 || ph < 2) {
        return {
          originX: 0,
          originY: 0,
          pw: host.clientWidth || hostRect.width,
          ph: host.clientHeight || hostRect.height,
        };
      }
      return {
        originX: x0 - hostRect.left,
        originY: y0 - hostRect.top,
        pw,
        ph,
      };
    },
    /**
     * 将画布在左右栏之间的可见工作区居中（transform-origin 为 0 0）
     */
    getDefaultCenteredViewportState(scale) {
      let s = scale != null ? Number(scale) : 1;
      if (!isFinite(s) || s <= 0) {
        s = 1;
      }
      const box = this.getVisibleWorkspaceBox();
      if (!box || box.pw < 2 || box.ph < 2) {
        return { scale: s, left: "0px", top: "0px" };
      }
      const W = this.editorCanvasWidthPx;
      const H = this.editorCanvasHeightPx;
      const left = Math.round(box.originX + (box.pw - W * s) / 2);
      const top = Math.round(box.originY + (box.ph - H * s) / 2);
      return { scale: s, left: `${left}px`, top: `${top}px` };
    },
    showLoading() {
      if (this.loadingInstance) {
        return; // 如果已经有加载动画，避免重复创建
      }
      this.loadingInstance = Loading.service({
        target: this.$el, // 加载动画渲染在当前组件对应的dom元素上
        text: this.loadingTexts[this.currentTextIndex],
      });

      // 每秒更新一次加载文字
      this.timer = setInterval(() => {
        var ind = (this.currentTextIndex + 1) % this.loadingTexts.length
        if (!this.isInitLoaded) {
          this.currentTextIndex = this.currentTextIndex + 1 > 2 ? this.currentTextIndex : ind;
        } else {
          this.currentTextIndex = this.currentTextIndex + 1 === this.loadingTexts.length ? this.currentTextIndex : ind;
        }
        this.loadingInstance.setText(this.loadingTexts[this.currentTextIndex]);
      }, 1000);

      // 兜底：避免某些异常时序导致 loading 永不关闭
      if (this.loadingSafetyTimer) {
        clearTimeout(this.loadingSafetyTimer);
      }
      this.loadingSafetyTimer = setTimeout(() => {
        if (!this.isInitLoaded) {
          console.warn("Editor loading timeout fallback triggered");
        }
        this.hideLoading();
      }, 15000);
    },
    hideLoading() {
      if (this.loadingInstance) {
        this.loadingInstance.close();
        this.loadingInstance = null;
      }
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
      if (this.loadingSafetyTimer) {
        clearTimeout(this.loadingSafetyTimer);
        this.loadingSafetyTimer = null;
      }
    },
    keydownHandle(e) {
      if (!document.getElementById("editor")) {
        return;
      }
      e = window.event || e;
      if (isTypingTarget(e) || isTextInputActive()) {
        return;
      }
      if (this.selectTypeVisible && e.keyCode === 27) {
        this.cancelSelectType();
        return;
      }
      if (!this.isShowArea && (!this.curComponent || this.curComponent.id === 'Back')) {
        return;
      }
      switch(e.keyCode) {
        case 37:// 左
          if(this.isShowArea) {
            this.handleAreaMove({ missX: -1 })
            this.$store.commit("recordSnapshot");
            break;
          }
          let finalLeft = this.curComponent.style.left - 1;
          if (finalLeft < 0) {
            finalLeft = 0;
          }
          if (finalLeft !== this.curComponent.style.left) {
            this.$store.commit("setShapeStyle", {
              left: finalLeft
            });
          }
          break;
        case 38:// 上
          if(this.isShowArea) {
            this.handleAreaMove({ missY: -1 })
            this.$store.commit("recordSnapshot");
            break;
          }
          let finalTop = this.curComponent.style.top - 1;
          if (finalTop < 0) {
            finalTop = 0;
          }
          if (finalTop !== this.curComponent.style.top) {
            this.$store.commit("setShapeStyle", {
              top: finalTop
            });
          }
          break;
        case 39:// 右
          if(this.isShowArea) {
            this.handleAreaMove({ missX: 1 })
            this.$store.commit("recordSnapshot");
            break;
          }
          let left = this.curComponent.style.left + 1;
          if ((left + this.curComponent.style.width) > this.projectStyleData.width) {
            left = this.projectStyleData.width - this.curComponent.style.width;
          }
          if (left !== this.curComponent.style.left) {
            this.$store.commit("setShapeStyle", {
              left: left
            });
          }
          break;
        case 40:// 下
          if(this.isShowArea) {
            this.handleAreaMove({ missY: 1 })
            this.$store.commit("recordSnapshot");
            break;
          }
          let top = this.curComponent.style.top + 1;
          if ((top + this.curComponent.style.height) > this.projectStyleData.height) {
            top = this.projectStyleData.height - this.curComponent.style.height;
          }
          if (top !== this.curComponent.style.top) {
            this.$store.commit("setShapeStyle", {
              top: top
            });
          }
          break;
      }
    },
    setEditorReSize() {
      const parentElem = this.$el.parentElement;
      const parentW = parentElem.clientWidth;
      const parentH = parentElem.clientHeight;
      const currentActualW = this.$el.clientWidth;
      const currentActualH = this.$el.clientHeight;
      const wPercent = parseFloat((parentW / currentActualW).toFixed(2));
      const hPercent = parseFloat((parentH / currentActualH).toFixed(2));
      let finalPer;
      // 取小一点的比例，来保证完全展示在页面中
      if (wPercent < hPercent) {
        finalPer = wPercent;
      } else {
        finalPer = hPercent;
      }
      this.$el.setAttribute('data-scale', finalPer);
      // this.$el.style.transform = `scale(${finalPer}, ${finalPer}) translate(-50%, -50%)`;
    },
    /** 框选/坐标换算用：与滚轮缩放闭包一致，避免 scale 不同步 */
    getEditorInteractionScale() {
      if (this._canvasScaleRef && this._canvasScaleRef.value > 0) {
        return this._canvasScaleRef.value;
      }
      const s = this.currentScale;
      return s > 0 ? s : 1;
    },
    getPosition(e) {
      // 拿到画布的位置信息
      const rectInfo = this.editor.getBoundingClientRect();
      this.editorX = rectInfo.left;
      this.editorY = rectInfo.top;
      const currentScale = this.getEditorInteractionScale();
      const startX = e.clientX;
      const startY = e.clientY;
      return {
        x: (startX - this.editorX) / currentScale,
        y: (startY - this.editorY) / currentScale
      }
    },
    handleMouseDown(e) {
      // 空格拖拽平移画布时不要开始框选，否则松手会 createGroup 误选中组件
      if (this.isSpaceNoShowArea) {
        return;
      }
      // 右键点击不可拖拽选择区域
      if(e.button === 2) {
        // 如果是右键点击，且在多选区域里 不做处理
        let isInArea = false;
        const { x, y } = this.getPosition(e);
        if(this.areaData) {
          const { style, components } = this.areaData
          if(components.length){
            const { left, top, width, height } = style
            if((x >= left && x <= left + width)
            && (y >= top && y <= top + height)) {
              isInArea = true;
            }
          }
        }
        if(!isInArea) {
          this.hideArea()
        }
        return;
      }
      // 点到画布空白（Grid/背景），Shape 会 stopPropagation 故不会走到这里
      this.$store.commit("setClickComponentStatus", false);
      // 如果没有选中组件 在画布上点击时需要调用 e.preventDefault() 防止触发 drop 事件
      if (
        !this.curComponent ||
        (this.curComponent.component != "v-text" &&
          this.curComponent.component != "rect-shape")
      ) {
        e.preventDefault();
      }
      
      this.hideArea();

      const readEditorOrigin = () => {
        const rectInfo = this.editor.getBoundingClientRect();
        this.editorX = rectInfo.left;
        this.editorY = rectInfo.top;
      };
      readEditorOrigin();
      const currentScale = this.getEditorInteractionScale();
      const startX = e.clientX;
      const startY = e.clientY;
      this.start.x = (startX - this.editorX) / currentScale;
      this.start.y = (startY - this.editorY) / currentScale;
      // 展示选中区域
      this.isShowArea = true;

      const move = (moveEvent) => {
        readEditorOrigin();
        const s = this.getEditorInteractionScale();
        this.width = Math.abs(moveEvent.clientX - startX) / s;
        this.height = Math.abs(moveEvent.clientY - startY) / s;
        if (moveEvent.clientX < startX) {
          this.start.x = (moveEvent.clientX - this.editorX) / s;
        }

        if (moveEvent.clientY < startY) {
          this.start.y = (moveEvent.clientY - this.editorY) / s;
        }
        // console.log('movex-----', this.start.x, this.width, startX, moveEvent.clientX, currentScale);
        // console.log('movey-----', this.start.y, this.height, startY, moveEvent.clientY);
      };

      const up = (e) => {
        document.removeEventListener("mousemove", move);
        document.removeEventListener("mouseup", up, true);

        if (this.isSpaceNoShowArea) {
          this.hideArea();
          return;
        }

        if (e.clientX == startX && e.clientY == startY) {
          this.hideArea();
          return;
        }

        this.createGroup();
      };

      document.addEventListener("mousemove", move);
      document.addEventListener("mouseup", up, true);
    },

    hideArea() {
      this.isShowArea = 0;
      this.width = 0;
      this.height = 0;
      this.selectTypeVisible = false;
      this.selectTypeOptions = [];
      this.pendingSelectComponents = [];

      this.$store.commit("setAreaData", {
        style: {
          left: 0,
          top: 0,
          width: 0,
          height: 0,
        },
        components: [],
      });
    },

    isComponentMultiSelected(id) {
      if (id == null) {
        return false;
      }
      const list = (this.areaData && this.areaData.components) || [];
      return list.some((c) => c && String(c.id) === String(id));
    },

    resolveLiveComponent(payload) {
      const id = payload && (payload.id != null ? payload.id : payload);
      if (id == null) {
        return null;
      }
      return (this.componentData || []).find(
        (c) => c && String(c.id) === String(id)
      );
    },

    toggleShiftSelect(payload) {
      this.$store.commit("hideContextMenu");
      const target = this.resolveLiveComponent(payload);
      if (!target || target.id === "Back") {
        return;
      }
      if (target.isLock) {
        return;
      }
      const areaList = (this.areaData && this.areaData.components) || [];
      let selected = areaList
        .map((c) => this.resolveLiveComponent(c))
        .filter(Boolean);
      if (!selected.length && this.curComponent && this.curComponent.id !== "Back") {
        const cur = this.resolveLiveComponent(this.curComponent);
        if (cur) {
          selected = [cur];
        }
      }
      const idx = selected.findIndex((c) => String(c.id) === String(target.id));
      if (idx >= 0) {
        selected.splice(idx, 1);
      } else {
        selected.push(target);
      }
      this.applyComponentSelection(selected);
    },

    applyComponentSelection(components) {
      const list = (components || []).filter(
        (c) => c && c.id && c.id !== "Back" && !c.isLock
      );
      if (list.length === 0) {
        this.hideArea();
        this.$store.commit("setCurComponent", {
          component: null,
          index: null,
        });
        return;
      }
      if (list.length === 1) {
        const component = list[0];
        const index = this.componentData.findIndex(
          (c) => c && String(c.id) === String(component.id)
        );
        this.$store.commit("setCurComponent", {
          component,
          index: index === -1 ? null : index,
        });
        this.$store.commit("setClickComponentStatus", true);
        this.hideArea();
        eventBus.$emit("canvasAiTarget", { id: component.id, source: "select-one" });
        return;
      }
      let top = Infinity;
      let left = Infinity;
      let right = -Infinity;
      let bottom = -Infinity;
      list.forEach((component) => {
        const style = getComponentRotatedStyle(component.style);
        if (style.left < left) left = style.left;
        if (style.top < top) top = style.top;
        if (style.right > right) right = style.right;
        if (style.bottom > bottom) bottom = style.bottom;
      });
      this.start.x = left;
      this.start.y = top;
      this.width = right - left;
      this.height = bottom - top;
      this.isShowArea = true;
      this.$store.commit("setCurComponent", {
        component: null,
        index: null,
      });
      this.$store.commit("setAreaData", {
        style: {
          left,
          top,
          width: this.width,
          height: this.height,
        },
        components: list,
      });
      this.$store.commit("setClickComponentStatus", true);
      eventBus.$emit("canvasAiTarget", {
        ids: list.map((c) => c.id),
        source: "area",
      });
    },

    createGroup() {
      const areaData = this.getSelectArea();
      if (areaData.length === 0) {
        this.hideArea();
        return;
      }
      const typeOptions = this.buildSelectTypeOptions(areaData);
      if (areaData.length > 1 && typeOptions.length > 1) {
        this.pendingSelectComponents = areaData;
        this.selectTypeOptions = typeOptions;
        this.selectTypeVisible = true;
        this.applyComponentSelection(areaData);
        return;
      }
      this.selectTypeVisible = false;
      this.selectTypeOptions = [];
      this.pendingSelectComponents = [];
      this.applyComponentSelection(areaData);
    },

    getBoxSelectTypeKey(comp) {
      if (!comp) {
        return "";
      }
      if (comp.component === "v-button") {
        return `v-button::${comp.type || "square"}`;
      }
      return String(comp.component || "");
    },

    getBoxSelectTypeLabel(comp) {
      if (!comp) {
        return "组件";
      }
      return comp.label || comp.propValue || comp.component || "组件";
    },

    buildSelectTypeOptions(list) {
      const map = {};
      (list || []).forEach((comp) => {
        const key = this.getBoxSelectTypeKey(comp);
        if (!key) {
          return;
        }
        if (!map[key]) {
          map[key] = {
            key,
            label: this.getBoxSelectTypeLabel(comp),
            count: 0,
          };
        }
        map[key].count += 1;
      });
      return Object.values(map).sort((a, b) => b.count - a.count);
    },

    confirmSelectType(typeKey) {
      const all = this.pendingSelectComponents || [];
      const next = typeKey
        ? all.filter((c) => this.getBoxSelectTypeKey(c) === typeKey)
        : all;
      this.selectTypeVisible = false;
      this.selectTypeOptions = [];
      this.pendingSelectComponents = [];
      this.applyComponentSelection(next);
    },

    cancelSelectType() {
      this.hideArea();
      this.$store.commit("setCurComponent", {
        component: null,
        index: null,
      });
      this.$store.commit("setClickComponentStatus", false);
    },

    getSelectArea() {
      // console.log('getselectarea--', this.start.x, this.width, '-----', this.start.y, this.height);
      const result = [];
      const { x, y } = this.start;
      const selLeft = x;
      const selTop = y;
      const selRight = x + this.width;
      const selBottom = y + this.height;
      const eps = 0.5;
      this.usableComponentsData.forEach((component) => {
        if (component.isLock) return;

        const { left, top, width, height } = getComponentRotatedStyle(
          component.style
        );
        const compLeft = left;
        const compTop = top;
        const compRight = left + width;
        const compBottom = top + height;
        const intersect =
          compLeft < selRight + eps &&
          compRight > selLeft - eps &&
          compTop < selBottom + eps &&
          compBottom > selTop - eps;
        if (intersect) {
          result.push(component);
        }
      });
      return result;
    },

    handleContextMenu(e) {
      e.stopPropagation();
      const menuW = 160;
      const menuH = 360;
      let top = e.clientY;
      let left = e.clientX;
      const maxLeft = Math.max(0, window.innerWidth - menuW);
      const maxTop = Math.max(0, window.innerHeight - menuH);
      if (left > maxLeft) left = maxLeft;
      if (top > maxTop) top = maxTop;
      if (left < 0) left = 0;
      if (top < 0) top = 0;
      const shapeEl = this.findShapeFromEvent(e);
      if (shapeEl) {
        const shapeId = shapeEl.getAttribute("data-shape-id");
        if (shapeId) {
          const list = this.componentData || [];
          const index = list.findIndex(
            (c) => c && String(c.id) === String(shapeId)
          );
          if (index !== -1) {
            this.$store.commit("setClickComponentStatus", true);
            const areaList = (this.areaData && this.areaData.components) || [];
            const inArea = areaList.some(
              (c) => c && String(c.id) === String(shapeId)
            );
            if (!inArea) {
              this.$store.commit("setCurComponent", {
                component: list[index],
                index,
              });
              eventBus.$emit("hideArea");
            }
          }
        }
      }
      const pastePoint = clientPointToCanvas(e.clientX, e.clientY);
      this.$store.commit("showContextMenu", {
        top,
        left,
        pasteCanvasTop: pastePoint.top,
        pasteCanvasLeft: pastePoint.left,
      });
    },

    findShapeFromEvent(e) {
      const x = e.clientX;
      const y = e.clientY;
      const stack = document.elementsFromPoint(x, y) || [];
      for (let i = 0; i < stack.length; i++) {
        const el = stack[i];
        if (!el) {
          continue;
        }
        if (el.classList && el.classList.contains("shape")) {
          return el;
        }
        if (el.closest) {
          const shapeEl = el.closest(".shape");
          if (shapeEl) {
            return shapeEl;
          }
        }
      }
      const shapes = this.$el
        ? this.$el.querySelectorAll(".shape[data-shape-id]")
        : [];
      for (let i = shapes.length - 1; i >= 0; i--) {
        const node = shapes[i];
        if (!node || !node.getBoundingClientRect) {
          continue;
        }
        const rect = node.getBoundingClientRect();
        if (
          x >= rect.left &&
          x <= rect.right &&
          y >= rect.top &&
          y <= rect.bottom
        ) {
          return node;
        }
      }
      return null;
    },

    getShapeStyle(style) {
      const result = {};
      ["width", "height", "top", "left", "rotate"].forEach((attr) => {
        if (attr != "rotate") {
          result[attr] = style[attr] + "px";
        } else {
          result.transform = "rotate(" + style[attr] + "deg)";
        }
      });
      if (!isEmpty(style.zIndex)) {
        result.zIndex = style.zIndex;
      }
      return result;
    },

    getComponentStyle(style, item) {
      let styleObj = getStyle(style, ["top", "left", "width", "height", "rotate"]);
      if (item && item.component === 'StatusLight') {
        // 状态灯
        if (style.background === 'icon' && style.backgroundImage) {
          delete styleObj.backgroundColor;
        } else {
          delete styleObj.backgroundImage;
        }
      }
      if (item && item.component === 'Visualization') {
        // 可视化组件
        if (style.backgroundType === 'icon' && style.backgroundImage) {
          delete styleObj.backgroundColor;
        } else {
          delete styleObj.backgroundImage;
        }
      }
      if (item && item.component === "v-button") {
        styleObj = withTransparentButtonColor(styleObj, style, false);
      }
      return styleObj;
    },

    handleInput(element, value) {
      // 根据文本组件高度调整 shape 高度
      this.$store.commit("setShapeStyle", {
        height: this.getTextareaHeight(element, value),
      });
    },

    getTextareaHeight(element, text) {
      let { lineHeight, fontSize, height } = element.style;
      if (lineHeight === "") {
        lineHeight = 1.5;
      }

      const newHeight = (text.split("<br>").length - 1) * lineHeight * fontSize;
      return height > newHeight ? height : newHeight;
    },
    handleAreaMove(moveInfo) {
      console.log('areamove-----', moveInfo)
      var pageWidth, pageHeight;
      var pageStyle = this.componentData.find((a) => a.id === 'Back');
      if (this.isChildPageCanvas) {
        // 是子页面
        pageWidth = pageStyle.style.pageWidth;
        pageHeight = pageStyle.style.pageHeight;
      } else {
        pageWidth = this.projectStyleData.width;
        pageHeight = this.projectStyleData.height;
      }

      if('missX' in moveInfo) {
        const afterX = this.start.x + moveInfo.missX
        if(afterX < 0) {
          // 如果向左导致x小于0， 不能再往左拖动
          moveInfo.missX = -this.start.x
        }else if(afterX + this.width > pageWidth) {
          // 如果拖动后右侧超出右边框
          moveInfo.missX = pageWidth - this.width - this.start.x
        }
        this.start.x += moveInfo.missX;
      }
      if('missY' in moveInfo) {
        const afterY = this.start.y + moveInfo.missY
        if(afterY < 0) {
          // 如果向上导致y小于0， 不能再往上拖动
          moveInfo.missY = -this.start.y
        }else if(afterY + this.height > pageHeight) {
          // 如果拖动后右侧超出右边框
          moveInfo.missY = pageHeight - this.height - this.start.y
        }
        this.start.y += moveInfo.missY;
      }
      // 修改当前组件样式
      this.$store.commit("setAreaStyle", moveInfo);
    },
    // Alt + 滚轮缩放（以鼠标位置为锚点）、空格 + 拖拽移动；仅绑定在当前 Editor 根节点，避免重复监听
    bindCanvasInteraction() {
      if (this._canvasInteractBound) {
        return;
      }
      this._canvasInteractBound = true;
      const myDiv = this.$el;
      const vm = this;
      this._canvasScaleRef = {
        value:
          this.currentScale && this.currentScale > 0 ? this.currentScale : 1,
      };
      let isAltPressed = false;
      let isMouseOver = false;
      let mousedownStatus = false;
      let isDragging = false;
      let startX;
      let startY;
      let divStartX;
      let divStartY;

      const readPan = () => {
        let L = parseFloat(myDiv.style.left);
        let T = parseFloat(myDiv.style.top);
        if (Number.isNaN(L)) {
          L = 0;
        }
        if (Number.isNaN(T)) {
          T = 0;
        }
        return { L, T };
      };

      const applyScaleTransform = () => {
        const ref = vm._canvasScaleRef;
        if (!ref) {
          return;
        }
        const sc = ref.value;
        myDiv.style.transformOrigin = "0 0";
        myDiv.style.transform = `scale(${sc})`;
        myDiv.setAttribute("data-scale", String(sc));
      };

      const keydownFun = (e) => {
        if (isTypingTarget(e) || isTextInputActive()) {
          return;
        }
        if (e.key === "Alt" && isMouseOver) {
          isAltPressed = true;
        }
        if (e.key === "Alt") {
          e.preventDefault();
        }
        if (e.code === "Space" || e.key === " ") {
          if (!isCanvasPanHotkey(e)) {
            return;
          }
          isDragging = true;
          this.isSpaceNoShowArea = true;
          myDiv.setAttribute("data-space-pan", "1");
          myDiv.style.cursor = "grab";
          if (mousedownStatus && isDragging) {
            myDiv.style.cursor = "grabbing";
          }
          e.preventDefault();
        }
      };

      const resetSpacePan = () => {
        isDragging = false;
        mousedownStatus = false;
        vm.isSpaceNoShowArea = false;
        myDiv.removeAttribute("data-space-pan");
        myDiv.style.cursor = "default";
      };

      const keyupFun = (e) => {
        if (e.key === "Alt") {
          isAltPressed = false;
        }
        if (e.code === "Space" || e.key === " ") {
          resetSpacePan();
        }
      };

      const onEditorMouseEnter = () => {
        isMouseOver = true;
      };

      const onEditorMouseLeave = () => {
        isMouseOver = false;
        isAltPressed = false;
      };

      const onWheel = (e) => {
        if (!e.altKey && !isAltPressed) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        const ref = vm._canvasScaleRef;
        if (!ref) {
          return;
        }
        const oldScale = ref.value;
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        this.boxdeltaY = e.deltaY;
        const nextScale = ref.value * delta;
        if (!isFinite(nextScale) || nextScale <= 0) {
          return;
        }
        ref.value = nextScale;
        if (ref.value === oldScale) {
          return;
        }
        const parentEl = myDiv.offsetParent;
        if (parentEl) {
          const pr = parentEl.getBoundingClientRect();
          const mx = e.clientX - pr.left;
          const my = e.clientY - pr.top;
          const { L, T } = readPan();
          const ratio = ref.value / oldScale;
          const newL = mx - (mx - L) * ratio;
          const newT = my - (my - T) * ratio;
          myDiv.style.left = `${newL}px`;
          myDiv.style.top = `${newT}px`;
        }
        vm.currentScale = ref.value;
        applyScaleTransform();
        vm.scheduleCanvasViewBump();
      };

      const onDocMouseDown = (e) => {
        if (!isDragging || e.button !== 0) {
          return;
        }
        const t = e.target;
        if (
          !t ||
          typeof t.closest !== "function" ||
          !t.closest("#editor, #content")
        ) {
          return;
        }
        mousedownStatus = true;
        startX = e.clientX;
        startY = e.clientY;
        const { L, T } = readPan();
        divStartX = L;
        divStartY = T;
        myDiv.style.cursor = "grabbing";
        e.preventDefault();
        e.stopPropagation();
      };

      const onDocMouseMove = (e) => {
        if (!isDragging || !mousedownStatus) {
          return;
        }
        const newX = divStartX + (e.clientX - startX);
        const newY = divStartY + (e.clientY - startY);
        myDiv.style.left = `${newX}px`;
        myDiv.style.top = `${newY}px`;
        vm.scheduleCanvasViewBump();
      };

      const onDocMouseUp = () => {
        mousedownStatus = false;
        if (isDragging) {
          myDiv.style.cursor = "grab";
        } else {
          myDiv.style.cursor = "default";
        }
        vm.scheduleCanvasViewBump();
      };

      document.addEventListener("keydown", keydownFun, true);
      document.addEventListener("keyup", keyupFun, true);
      myDiv.addEventListener("mouseenter", onEditorMouseEnter);
      myDiv.addEventListener("mouseleave", onEditorMouseLeave);
      myDiv.addEventListener("wheel", onWheel, { passive: false });
      document.addEventListener("mousedown", onDocMouseDown, true);
      document.addEventListener("mousemove", onDocMouseMove);
      document.addEventListener("mouseup", onDocMouseUp);

      applyScaleTransform();
      this.currentScale = vm._canvasScaleRef
        ? vm._canvasScaleRef.value
        : 1;
      vm.scheduleCanvasViewBump();

      this._canvasInteractHandles = {
        keydownFun,
        keyupFun,
        resetSpacePan,
        onEditorMouseEnter,
        onEditorMouseLeave,
        onWheel,
        onDocMouseDown,
        onDocMouseMove,
        onDocMouseUp,
        myDiv,
      };
    },

    unbindCanvasInteraction() {
      const h = this._canvasInteractHandles;
      if (!h) {
        this._canvasInteractBound = false;
        return;
      }
      document.removeEventListener("keydown", h.keydownFun, true);
      document.removeEventListener("keyup", h.keyupFun, true);
      h.myDiv.removeEventListener("mouseenter", h.onEditorMouseEnter);
      h.myDiv.removeEventListener("mouseleave", h.onEditorMouseLeave);
      h.myDiv.removeEventListener("wheel", h.onWheel, { passive: false });
      document.removeEventListener("mousedown", h.onDocMouseDown, true);
      document.removeEventListener("mousemove", h.onDocMouseMove);
      document.removeEventListener("mouseup", h.onDocMouseUp);
      this._canvasInteractHandles = null;
      this._canvasInteractBound = false;
      this._canvasScaleRef = null;
    },
  },
};
</script>

<style lang="scss">
.link-select-hover {
  opacity: 0.3 !important;
  background-color: rgb(64, 158, 255) !important;
}
</style>
<style lang="scss" scoped>
.editor {
  // position: absolute;
  position: relative;
  transform-origin: 0 0;
  background: #fff;
  overflow: visible;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.22), 0 6px 18px rgba(0, 0, 0, 0.12);
  // margin: auto;
  // left: 50%;
  // top: 50%;
  .lock {
    opacity: 0.5;

    &:hover {
      cursor: not-allowed;
    }
  }
}
.canvas-size-marks {
  position: absolute;
  left: 0;
  top: 0;
  width: 0;
  height: 0;
  z-index: 6;
  pointer-events: none;
  color: #000;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  font-family: Arial, Helvetica, sans-serif;
}
.canvas-size-w {
  position: absolute;
  left: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.canvas-size-h {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  span {
    writing-mode: vertical-rl;
    text-orientation: upright;
    letter-spacing: 0;
  }
}
.canvas-size-tick {
  background: #000;
  flex: none;
}
.canvas-size-tick-h {
  width: 22px;
  height: 2px;
  margin-top: 4px;
}
.canvas-size-tick-v {
  width: 2px;
  height: 22px;
  margin-left: 4px;
}
.lowerZ {
  z-index: 1 !important;
}
.commonZ {
  /* 层级由组件 style.zIndex 控制，避免全部压成同一层导致无法点选下层按钮/文字 */
}
.select-type-panel {
  position: absolute;
  z-index: 30;
  min-width: 180px;
  max-width: 260px;
  max-height: 240px;
  overflow: auto;
  padding: 8px;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  pointer-events: auto;
}
.select-type-title {
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}
.select-type-btn {
  display: block;
  width: 100%;
  margin-bottom: 4px;
  padding: 6px 10px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  color: #303133;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  &:hover {
    border-color: #1c92ff;
    color: #1c92ff;
    background: #ecf5ff;
  }
  &.all {
    font-weight: 600;
  }
  &.cancel {
    margin-top: 6px;
    margin-bottom: 0;
    border-color: #e4e7ed;
    color: #909399;
    text-align: center;
    background: #f5f7fa;
    &:hover {
      border-color: #f56c6c;
      color: #f56c6c;
      background: #fef0f0;
    }
  }
  &:last-child {
    margin-bottom: 0;
  }
}
.edit {
  .component {
    outline: none;
    width: 100%;
    height: 100%;
  }
}
.editor[data-space-pan="1"] ::v-deep .area {
  pointer-events: none;
}
</style>
